if (!customElements.get('cart-notification')) {
  class CartNotification extends HTMLElement {
    constructor() {
      super();

      this.onBodyClick = this.handleBodyClick.bind(this);
      this.onCarouselScroll = this.updateCarousel.bind(this);
      this.onCarouselPrev = () => this.scrollCarousel(-1);
      this.onCarouselNext = () => this.scrollCarousel(1);
      this.keepUpsell = false;
      this.loadedProductId = null;
      this.bodyClickTimer = null;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.notification = this.querySelector('#cart-notification');
      this.header = document.querySelector('sticky-header');
      this.upsell = this.querySelector('[data-cart-upsell]');
      this.upsellProducts = this.querySelector('[data-cart-upsell-products]');

      if (!this.notification) return;

      this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      this.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-cart-open-drawer]');
        if (!opener) return;
        event.preventDefault();
        this.openCartDrawer(opener);
      });
      this.querySelectorAll('[data-cart-popup-close]').forEach((closeButton) =>
        closeButton.addEventListener('click', (event) => {
          if (closeButton.tagName === 'A') return;
          event.preventDefault();
          this.close();
        })
      );

      this.addEventListener(
        'submit',
        (event) => {
          if (event.target.closest('[data-cart-upsell]')) this.keepUpsell = true;
        },
        true
      );

      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
      }
    }

    isMobile() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    open() {
      if (!this.notification) {
        this.notification = this.querySelector('#cart-notification');
      }
      if (!this.notification) return;

      this.notification.classList.add('animate', 'active');
      this.classList.add('is-open');
      this.querySelector('[name="checkout"]')?.removeAttribute('disabled');

      if (this.isMobile()) {
        document.body.classList.add('overflow-hidden', 'cart-popup-open');
      }

      this.notification.addEventListener(
        'transitionend',
        () => {
          this.notification.focus();
          try {
            trapFocus(this.notification);
          } catch (error) {
            // Focus trap is optional
          }
        },
        { once: true }
      );

      window.clearTimeout(this.bodyClickTimer);
      if (!this.isMobile()) {
        this.bodyClickTimer = window.setTimeout(() => {
          document.body.addEventListener('click', this.onBodyClick);
        }, 80);
      }

      this.dispatchCartViewEvent();
    }

    async dispatchCartViewEvent() {
      const { CartViewEvent } = window.StandardEvents || {};
      if (!CartViewEvent) return;

      try {
        const response = await fetch(`${routes.cart_url}.json`);
        const cart = await response.json();
        if (!cart?.currency) return;

        this.dispatchEvent(
          new CartViewEvent({
            context: 'dialog',
            cart: CartViewEvent.createCartFromAjaxResponse(cart),
          })
        );
      } catch (e) {
        // cart:view is informational; swallow fetch errors
      }
    }

    close() {
      window.clearTimeout(this.bodyClickTimer);
      this.notification.classList.remove('active');
      this.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden', 'cart-popup-open');
      document.body.removeEventListener('click', this.onBodyClick);
      removeTrapFocus(this.activeElement);
    }

    openCartDrawer(trigger) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') {
        window.location.assign(trigger?.href || window.routes?.cart_url || '/cart');
        return;
      }

      this.close();
      drawer.syncEmptyState?.(false);
      const refresh = drawer.querySelector('cart-drawer-items')?.onCartUpdate?.();
      Promise.resolve(refresh)
        .catch(() => {})
        .finally(() => {
          drawer.syncEmptyState?.(false);
          drawer.open(trigger);
        });
    }

    renderContents(parsedState) {
      this.cartItemKey = parsedState.key;
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = document.getElementById(section.id);
        if (!sectionElement || !parsedState.sections?.[section.id]) return;
        let html = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        if (!html && section.id === 'cart-notification-product') {
          html = this.getSectionInnerHTML(parsedState.sections[section.id], '.cart-item');
        }
        if (html) sectionElement.innerHTML = html;
      });

      this.header?.reveal?.();
      this.applyAddedQuantity(parsedState);

      if (!this.isMobile()) {
        this.open();
        return Promise.resolve();
      }

      return this.readyAndOpen(parsedState.product_id).catch(() => {
        this.hideUpsell();
        this.open();
      });
    }

    applyAddedQuantity(parsedState) {
      const qty = Number.parseInt(parsedState?.added_quantity ?? parsedState?.quantity, 10);
      if (!Number.isFinite(qty) || qty < 1) return;
      this.querySelectorAll('[data-cart-added-qty]').forEach((el) => {
        el.textContent = String(qty);
      });
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-notification-product',
          selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
        },
        {
          id: 'cart-notification-button',
        },
        {
          id: 'cart-icon-bubble',
        },
      ];
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      return parsed.querySelector(selector)?.innerHTML || parsed.querySelector('.shopify-section')?.innerHTML || '';
    }

    handleBodyClick(evt) {
      const target = evt.target;
      if (target !== this.notification && !target.closest('cart-notification')) {
        const disclosure = target.closest('details-disclosure, header-menu');
        this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
        this.close();
      }
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    async readyAndOpen(productId) {
      if (this.keepUpsell) {
        this.keepUpsell = false;
        this.filterAddedProduct(productId);
        this.initCarousel();
        this.open();
        return;
      }

      await this.loadUpsell(productId);
      this.open();
    }

    async loadUpsell(productId) {
      if (!this.isMobile()) return;

      if (!productId) {
        this.hideUpsell();
        return;
      }

      if (
        this.loadedProductId === String(productId) &&
        this.upsellProducts?.querySelector('[data-cart-slide]')
      ) {
        this.initCarousel();
        return;
      }

      const markup = await this.fetchRecommendations(productId, 'complementary');

      if (!markup) {
        this.hideUpsell();
        return;
      }

      this.upsellProducts.innerHTML = markup.html;
      this.loadedProductId = String(productId);
      this.filterAddedProduct(productId);
      this.initCarousel();
    }

    async fetchRecommendations(productId, intent = 'complementary') {
      const endpoint = this.dataset.recommendationsUrl;
      if (!endpoint) return null;

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 1500);

      try {
        const response = await fetch(
          `${endpoint}?section_id=cart-notification-upsell&product_id=${encodeURIComponent(
            productId
          )}&limit=5&intent=${encodeURIComponent(intent)}`,
          { signal: controller.signal }
        );
        if (!response.ok) return null;
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, 'text/html');
        const content = html.querySelector('[data-cart-upsell-products]');
        if (!content?.querySelector('[data-cart-slide]')) return null;
        return {
          html: content.innerHTML,
          title: content.getAttribute('data-upsell-title') || '',
        };
      } catch (e) {
        return null;
      } finally {
        window.clearTimeout(timeoutId);
      }
    }

    filterAddedProduct(productId) {
      if (!productId) return;
      this.querySelectorAll(`[data-product-id="${productId}"]`).forEach((card) => {
        card.closest('.cart-popup-products__item')?.remove();
      });
    }

    hideUpsell() {
      this.upsell?.setAttribute('hidden', '');
      this.notification?.classList.remove('has-upsell');
      this.notification?.classList.add('is-solo');
    }

    showUpsell() {
      this.upsell?.removeAttribute('hidden');
      this.notification?.classList.add('has-upsell');
      this.notification?.classList.remove('is-solo');
    }

    initCarousel() {
      this.track = this.querySelector('[data-cart-track]');
      this.slides = Array.from(this.querySelectorAll('[data-cart-slide]'));
      this.dotsRoot = this.querySelector('[data-cart-dots]');
      this.prevBtn = this.querySelector('[data-cart-prev]');
      this.nextBtn = this.querySelector('[data-cart-next]');
      this.nav = this.querySelector('[data-cart-carousel-nav]');

      if (!this.track || this.slides.length === 0) {
        this.hideUpsell();
        return;
      }

      this.showUpsell();
      this.renderDots();
      this.bindCarousel();
      this.updateCarousel();
    }

    bindCarousel() {
      this.track.removeEventListener('scroll', this.onCarouselScroll);
      this.prevBtn?.removeEventListener('click', this.onCarouselPrev);
      this.nextBtn?.removeEventListener('click', this.onCarouselNext);
      this.track.addEventListener('scroll', this.onCarouselScroll, { passive: true });
      this.prevBtn?.addEventListener('click', this.onCarouselPrev);
      this.nextBtn?.addEventListener('click', this.onCarouselNext);
    }

    renderDots() {
      if (!this.dotsRoot) return;
      this.dotsRoot.innerHTML = this.slides
        .map(
          (_, index) =>
            `<button type="button" class="cart-notification__dot" data-cart-dot="${index}" aria-label="${index + 1}"></button>`
        )
        .join('');
      this.dotsRoot.querySelectorAll('[data-cart-dot]').forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = Number(dot.dataset.cartDot);
          this.track.scrollTo({ left: index * this.track.clientWidth, behavior: 'smooth' });
        });
      });
      this.nav?.toggleAttribute('hidden', this.slides.length < 2);
    }

    scrollCarousel(direction) {
      if (!this.track) return;
      this.track.scrollBy({ left: direction * this.track.clientWidth, behavior: 'smooth' });
    }

    updateCarousel() {
      if (!this.track || !this.slides.length) return;
      const width = this.track.clientWidth || 1;
      const index = Math.min(
        this.slides.length - 1,
        Math.max(0, Math.round(this.track.scrollLeft / width))
      );
      this.dotsRoot?.querySelectorAll('[data-cart-dot]').forEach((dot, dotIndex) => {
        dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
      });
      this.prevBtn?.toggleAttribute('disabled', index <= 0);
      this.nextBtn?.toggleAttribute('disabled', index >= this.slides.length - 1);
    }
  }

  customElements.define('cart-notification', CartNotification);
}
